import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useDragControls, useMotionValue } from "framer-motion";
import BeraAIChat from "./BeraAIChat";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useKycModalOpen, useAnyOverlayOpen } from "@/lib/kycUiState";

export const BeraIAWidget = () => {
  const { t } = useTranslation();
  const isKycOpen = useKycModalOpen();
  const isAnyOverlayOpen = useAnyOverlayOpen();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"ai" | "conversas" | "alertas">("ai");
  const [selectedBookingChat, setSelectedBookingChat] = useState<any | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const location = useLocation();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const { user } = useAuth();
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getModalDimensions = () => {
    if (isDesktop) {
      // Fixed consistent size for desktop, no jumping around
      return { width: 420, height: 600 };
    } else {
      // Mobile: Elegant look with margins, preventing it from touching screen edges
      return { width: "calc(100vw - 32px)", height: "calc(100dvh - 120px)" };
    }
  };

  const dimensions = getModalDimensions();
  const isHostOnly = isHost && !isGuest && !isSuperAdmin;

  const isAnuncios = location.pathname === "/dashboard/anuncios";
  const isReservas = location.pathname === "/dashboard/reservas" || location.pathname === "/reservas";
  const isFinanceiro = location.pathname === "/dashboard/financeiro";
  const isDashboard = location.pathname.startsWith("/dashboard");

  const isOnboardingRoute = location.pathname.includes('/onboarding');
  const isAuthRoute = location.pathname.includes('/auth');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isInsurerRoute = location.pathname.startsWith('/seguradora') || location.pathname === '/seguradora/login';
  const isSuporteRoute = location.pathname === '/suporte';

  const showBottomTabsGlobal = !isOnboardingRoute && !isAuthRoute && !isAdminRoute && !isInsurerRoute && !isSuporteRoute;
  const isBottomTabVisible = showBottomTabsGlobal && !isAnuncios && !isReservas && !isFinanceiro && (isHostOnly ? (location.pathname === "/explorar" || isDashboard) : location.pathname === "/explorar");

  useEffect(() => {
    x.set(0);
    y.set(0);
  }, [isOpen, x, y]);
  const queryClient = useQueryClient();

  const { data: badgeData } = useQuery({
    queryKey: ["bera-ai-total-unread-messages", user?.id],
    queryFn: async () => {
      if (!user?.id) return { unread: 0, conversations: 0 };
      // Only count unread messages that belong to a real chat the user is part of.
      // Counting "bookings" as conversations was wrong — a user with bookings but
      // no chat messages would still see the badge.
      const { data: unreadRows } = await supabase
        .from("chat_messages")
        .select("booking_id")
        .eq("receiver_id", user.id)
        .eq("is_read", false);
      const rows = unreadRows || [];
      const distinctBookings = new Set(
        rows.map((r: any) => r.booking_id).filter(Boolean),
      );
      return {
        unread: rows.length,
        conversations: distinctBookings.size,
      };
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });
  const unreadMessagesCount = badgeData?.unread || 0;
  const hasConversations = (badgeData?.conversations || 0) > 0;

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel("widget-floating-unread-realtime")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "chat_messages",
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["bera-ai-total-unread-messages", user.id] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Escutar eventos globais para abrir o widget
  useEffect(() => {
    const handleOpenWidget = () => setIsOpen(true);
    window.addEventListener("open-bera-ia-widget", handleOpenWidget);
    return () => window.removeEventListener("open-bera-ia-widget", handleOpenWidget);
  }, []);

  // Sincronizar visibilidade da bottom bar mobile
  useEffect(() => {
    const event = new CustomEvent('berastays:bottom-tabs', { 
      detail: { hidden: isOpen } 
    });
    window.dispatchEvent(event);
    
    // Cleanup if component unmounts while open
    return () => {
      if (isOpen) {
        window.dispatchEvent(new CustomEvent('berastays:bottom-tabs', { detail: { hidden: false } }));
      }
    };
  }, [isOpen]);

  // Fechar o widget quando a rota muda, exceto se a rota for /suporte
  useEffect(() => {
    if (location.pathname !== '/suporte') {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [location.pathname]);

  const isHiddenPage = location.pathname.includes('/admin') || location.pathname.includes('/auth') || location.pathname.includes('/onboarding');

  if (isHiddenPage) return null;
  if (isKycOpen || isAnyOverlayOpen) return null;

  return (
    <>
      {/* Backdrop for click outside */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/10 z-[9998] pointer-events-auto"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Centering / Positioning flex wrapper */}
      <div 
        ref={constraintsRef}
        className={cn(
          "fixed inset-0 pointer-events-none z-[9999] flex",
          isOpen 
            ? "items-center justify-center p-4 md:items-end md:justify-end md:p-6" 
            : "items-end justify-end"
        )}
      >
        <motion.div 
          drag
          dragControls={dragControls}
          dragListener={!isOpen}
          dragConstraints={constraintsRef}
          dragMomentum={false}
          className={cn(
            "pointer-events-auto flex flex-col items-end",
            !isOpen && (isBottomTabVisible ? "mb-20 mr-4 md:mb-6 md:mr-6" : "mb-4 mr-4 md:mb-6 md:mr-6")
          )}
          style={{ touchAction: "none", x, y }}
        >
          {/* Janela de Chat */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  width: dimensions.width,
                  height: dimensions.height
                }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.35, type: "spring", damping: 28, stiffness: 260 }}
                className="w-full max-w-[400px] md:max-w-none bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <BeraAIChat 
                  onClose={() => setIsOpen(false)} 
                  onHeaderPointerDown={(e) => dragControls.start(e)}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  selectedBookingChat={selectedBookingChat}
                  setSelectedBookingChat={setSelectedBookingChat}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Botão Flutuante (Avatar Pulsante) */}
          <AnimatePresence>
            {!isOpen && (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(true)}
                className="relative group w-14 h-14 md:w-16 md:h-16 rounded-full overflow-visible shadow-xl"
                aria-label={t("beraAI.openAssistant") || "Abrir Bera — Assistente BeraStays"}
              >
                {/* Pulsing glow effect */}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#E8513D] to-[#D14430] rounded-full blur-lg opacity-40 group-hover:opacity-70 animate-pulse transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#E8513D]/50 to-[#D14430]/50 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
                
                {/* Avatar Image */}
                <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-white dark:border-gray-800 bg-black">
                  <img 
                    src="/bera-ia-avatar.png" 
                    alt="Bera — Assistente BeraStays" 
                    className="w-full h-full object-cover"
                    fetchPriority="high"
                    loading="eager"
                    decoding="sync"
                  />
                </div>

                {/* Unread badge indicator — only when there are conversations */}
                {hasConversations && unreadMessagesCount > 0 ? (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] text-[10px] font-extrabold bg-[#E8513D] text-white rounded-full flex items-center justify-center px-1 shadow-lg ring-2 ring-white animate-pulse z-30">
                    {unreadMessagesCount}
                  </span>
                ) : null}

                {/* Status indicator (online dot) */}
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 md:w-4 md:h-4 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" />
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
};

export default BeraIAWidget;
